import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { handleApiError } from '@/lib/api/axios'
import { mobilisationApi, mobilisationKeys } from '../api'
import { MemberList } from '../components/MemberList'

/**
 * "Confirm my team": for a team's lead or deputy during a mobilisation (Stage 4).
 *
 * The people here have each set a password and enrolled a device with the code sent to their
 * phone - and still cannot sign in. They get in when the lead, who knows them, says so.
 */
export function MyTeamPage() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const teams = useQuery({ queryKey: mobilisationKeys.mine, queryFn: mobilisationApi.mine, refetchInterval: 15_000 })
    const act = useMutation({ mutationFn: (work: () => Promise<unknown>) => work(), onSuccess: () => queryClient.invalidateQueries({ queryKey: mobilisationKeys.mine }) })
    return (
        <div className="space-y-6" style={{ padding: 'var(--spacing-page)' }} data-testid="my-team-page">
            <div>
                <h1 className="text-xl font-bold lg:text-2xl">{t('mobilisation.myTeam.title')}</h1>
                <p className="text-muted-foreground">{t('mobilisation.myTeam.description')}</p>
            </div>
            {teams.isLoading && <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>}
            {teams.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(teams.error)}</p>}
            {act.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(act.error)}</p>}
            {teams.data && teams.data.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-teams">{t('mobilisation.myTeam.none')}</p>}
            {(teams.data ?? []).map((team) => (
                <section key={`${team.mobilisationId}-${team.teamId}`} className="space-y-2" data-testid="my-team">
                    <h2 className="font-semibold">{team.teamName}</h2>
                    <MemberList
                        members={team.members}
                        mayDecide={() => true}
                        busy={act.isPending}
                        onConfirm={(member) => act.mutate(() => mobilisationApi.confirm(member.id))}
                        onRefuse={(member, reason) => act.mutate(() => mobilisationApi.refuse(member.id, reason))}
                        onResend={(member) => act.mutate(() => mobilisationApi.resend(member.id))}
                    />
                </section>
            ))}
        </div>
    )
}
